import { isMindshiftGuide } from '@/utils/guides';
import {
    MediaGuideMetadata,
    MediaProgramWithGuides,
} from '@/utils/media/types';
import { AudioPlayerControls } from '@rhg/components/media/audio-player/types';
import { useMemo } from 'react';

interface UseAudioControlsArgs {
    /** From `useProvidedProgramContext()`; may be undefined. */
    program?: Pick<
        MediaProgramWithGuides,
        'meditationType' | 'totalGuides' | 'isClinical' | 'dailyContentType'
    > | null;
    playingGuide: MediaGuideMetadata;
}

/**
 * Maps the current program + playing guide to an AudioPlayerControls value. The
 * enum value carries loop capability + nav style:
 * - `Playlist` — 3-state (Off→All→One), track-skip nav. Multi-track
 *   collections that aren't music: sleep-story playlists, DTC courses/series.
 * - `MusicAlbum` — same shape as `Playlist`, but no playback speed. Multi-
 *   track music albums.
 * - `SingleLoopable` — 2-state (Off→One), 15s-skip nav. Single SLEEP tracks
 *   only — mirrors iOS `isSleepStory` / Android `MeditationType.SLEEP`.
 * - `MusicSingle` — same shape as `SingleLoopable`, but no playback speed.
 *   Single-track music (EVE-13640): previously any `meditationType ===
 *   'music'` guide fell through to `Playlist` whenever `totalGuides` was
 *   absent or 1, producing track-skip Previous/Next buttons with no track to
 *   skip to. Music now only reaches `Playlist`'s multi-track sibling when
 *   `isMultiTrack` is actually true; every other music guide is `MusicSingle`.
 * - `Soundscape` / `Regular` — no loop button. Non-sleep single tracks (plain
 *   meditations, single DTC, undefined) get `Regular` — no single-track loop, per parity.
 *
 * Classification keys on the **multi-track signal** (`totalGuides > 1`),
 * mirroring Android (`isPlaylist` + guide count), NOT a content-specific
 * `meditationType` — the live API has no `'sequential'` value.
 */
export function useAudioControls({
    program,
    playingGuide,
}: UseAudioControlsArgs): AudioPlayerControls {
    // Derive the guide-level signals up front so the memo depends on stable
    // primitives rather than the whole `playingGuide` object — a reference
    // change with unchanged fields no longer re-runs the classifier.
    const isMindshift = isMindshiftGuide(playingGuide);
    const isTrailer = !!playingGuide?.isTrailer;
    const guideDailyContentType = playingGuide?.dailyContentType;

    return useMemo(() => {
        const type = program?.meditationType?.toLowerCase();
        const isMultiTrack = (program?.totalGuides ?? 0) > 1;
        // Dailies / DTC Series — truthy `dailyContentType` on the program or the
        // playing guide (e.g. 'calm' | 'trip' | 'move' | 'jay'). Not pack-based.
        const isDailies =
            !!program?.dailyContentType || !!guideDailyContentType;

        // No loop button: clinical, mindshift, trailers, DTC Series / Dailies.
        if (program?.isClinical || isMindshift || isTrailer || isDailies) {
            return AudioPlayerControls.Regular;
        }

        if (type === 'soundscape') {
            return AudioPlayerControls.Soundscape;
        }

        // Multi-track collection (track-skip nav): music album, sleep-story
        // playlist, DTC course/series — all get the full 3-state Loop All
        // cycle (EVE-11932 / EVE-13637; parity with Android). Music albums get
        // their own enum value (no playback speed); everything else shares
        // `Playlist`.
        if (isMultiTrack) {
            if (type === 'music') {
                return AudioPlayerControls.MusicAlbum;
            }
            return AudioPlayerControls.Playlist;
        }

        // Single-track music (EVE-13640): 2-state (Off→One) loop, 15s-skip
        // nav, no playback speed. A music guide reaching this line is never a
        // confirmed album — `isMultiTrack` already returned above — so
        // treating an unknown track count as single is the safe default.
        if (type === 'music') {
            return AudioPlayerControls.MusicSingle;
        }

        // Single SLEEP story: 2-state (Off→One) loop, 15s-skip nav. Only sleep content gets the single-track loop button
        if (type === 'sleep') {
            return AudioPlayerControls.SingleLoopable;
        }

        // Any other single track (plain meditation, single DTC, etc.): no loop
        // button, mirroring iOS/Android — non-sleep single tracks don't loop.
        return AudioPlayerControls.Regular;
    }, [
        program?.meditationType,
        program?.totalGuides,
        program?.isClinical,
        program?.dailyContentType,
        isMindshift,
        isTrailer,
        guideDailyContentType,
    ]);
}
